<template>
  <div v-if="showAlertForSmallScreen" class="alert-small-screen">
    <p>This site is only supported on larger screens. Please use a desktop or maximize your browser window.</p>
  </div>
  <div class="faq-container">
    <div class="faq-title">What is AGES?</div>
    <br>
    <div class="explanation-box-about">
      <p class="explanation-about">

        The long-term hope for ancient DNA has always been to not only revolutionize the study of human history but also
        to transform human biology and medicine. Specifically, ancient DNA offer the possibility in principle of
        revealing the selective forces that shaped traits and diseases over millennia.
        <br><br>
        AGES is a tool powered by ancient DNA sequences, designed to make the power of time series data publicly
        accessible for studying biology and evolution. AGES tracks genetic variation over time using advanced
        statistical methods to account for confounding factors<sup>1</sup>. It analyzes genetic data from over 8,000
        ancient and
        6,000 modern human samples from Western Eurasia, covering more than 10,000 years. These include populations from
        Europe and the Near East.
        <br><br>
        The great majority of the individuals are analyzed through targeted sequencing of approximately 1 million
        locations of the genome, leaving the rest of the genome largely unobserved. To address these gaps, we infer
        missing genotypes by taking advantage of the high redundancy of human patterns of genetic information.
        Specifically, we use state-of-the-art statistical methods that leverage information about how genetic variants
        are correlated in modern populations<sup>2,3</sup>.
        <br><br>
        With this dataset in hand, the methodology visualized in AGES allows us to test for consistent changes in allele
        frequencies over time, above and beyond what would be expected from the population genetic structure measured by
        the samples themselves<sup>1</sup>. Our test is simple: at each variant we ask if hypothesizing a non-zero
        selection
        coefficient—causing allele frequency to trend in the same direction over all times and places—predicts frequency
        differences across populations significantly better than empirically measured population structure alone.
        <br><br>
        AGES also integrates genetic variants with independent datasets on genes and phenotypes, allowing users to
        explore the selective pressures behind these changes. Currently, AGES focuses on Western Eurasian populations
        due to available data, but expansion to other regions is very important given how promising the methodology is.
        For further information on data generation and how AGES can support your research, please refer to our
        publication<sup>1</sup> and the <a class="resource" style="font-size: 16px; color: #A51C30; text-decoration: none;" href="/#/faq">FAQ</a> page.
      </p>
    </div>
    <!--
    <div class="faq-title">The AGES Team</div>
    <div class="team-section">
      <ul class="main-team">
        <li style="line-height: 25px;"><b>David Reich</b> is the Principal Investigator of the project. 
        </li>
        <li style="line-height: 25px;"><b>Ali Akbari</b> is a Senior Staff Scientist at Harvard Medical School, Harvard University, and the Broad Institute. He leads the project.
        </li>

        <li style="line-height: 25px;"><b>Mohammadreza Kariminejad</b> is an undergraduate computer scientist responsible for the full-stack development of our web application. He has independently designed and implemented both the front-end and back-end, developed algorithms for search and query optimization, and improved the application's performance and scalability.
        </li>
      </ul>
    </div>

    <br>

    <div class="team-section">
      <div class="team-title">Acknowledgments</div>
      <p class="acknowledgment-names">
        Alison R. Barton, Steven Gazal, Zheng Li, Annabel Perry, Yating Zeng, Alissa Mittnik, Nick Patterson, Matthew
        Mah, Xiang Zhou, Alkes L. Price, Eric S. Lander, Ron Pinhasi, Nadin Rohland, David Reich
      </p>
    </div>
-->
    <div class="faq-title">References</div>
    <ol class="reference">
      <li id="ref1">
        Akbari, A. <i>et al.</i> Pervasive findings of directional selection realize the promise of ancient DNA to elucidate
        human adaptation. 2024.09.14.613021 Preprint at <a class="resource" style="color: #A51C30; text-decoration: none; font-size: 16px;" target="_blank" href="http://doi.org/10.1101/2024.09.14.613021">doi.org/10.1101/2024.09.14.613021</a> (2024).
      </li>
      <li id="ref2">
        Rubinacci, S., Ribeiro, D. M., Hofmeister, R. J. & Delaneau, O. Efficient phasing and imputation of low-coverage sequencing data using large reference panels. Nat. Genet. <b>53</b>, 120-126 (2021).
      </li>
      <li>
        Sousa da Mota, B. et al. Imputation of ancient human genomes. Nat. Commun. <b>14</b>, 3660 (2023).
      </li>
    </ol>
  </div>

</template>

<script>
import { smallScreenMixin } from '@/mixins/smallScreenMixin';

export default {
  name: "About",
  mixins: [smallScreenMixin],
  methods: {
    scroll(_id, top = 90) {
      const manhattanDiv = document.getElementById(_id);
      if (manhattanDiv) {
        window.scrollTo({
          top: top,
          behavior: 'smooth'
        });

        manhattanDiv.classList.add("highlight");

        setTimeout(() => {
          manhattanDiv.classList.remove("highlight");
        }, 3000);
      }
    }
  },
  mounted() {
    const hashParts = window.location.hash.split("#");
    if (hashParts.length > 1) {
      let part = hashParts[hashParts.length - 1]
      this.scroll(part, 1100)
    }
  }
};
</script>

<style>
.faq-container {
  width: 945px;
  margin: 100px auto;
  text-align: left;
}

.faq-title {
  font-size: 24px;
  margin-top: 30px;
  font-weight: bold;
  margin-bottom: 20px;
}

.team-section {
  padding-left: 20px;
  margin-bottom: 20px;
}

.team-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 10px;
}

.main-team {
  list-style-type: none;
  padding-left: 0;
}

.main-team li {
  margin-bottom: 10px;
}

.acknowledgment-names {
  color: #555;
  line-height: 23px;
  text-align: justify;
  padding-left: 20px;
}

ul li {
  margin-left: 20px;
}

.alert-small-screen {
  font-size: 1.3rem;
  width: 90vw;
  margin: 0 auto;
  text-align: center;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.explanation-about {
  text-align: justify;
  line-height: 23px;
  position: relative;
  top: -20px;
  width: 945px;
}

.explanation-box-about {
  display: flex;
  justify-content: center;
  position: relative;
  padding: 0 !important;
}

.reference {
  line-height: 23px;
}

ol li {
  margin-left: 19px;
  margin-top: 30px;
  text-align: justify;
}

.faq-question {
  font-weight: bold;
  scroll-behavior: smooth;
  margin-top: 20px;
  text-align: justify;
}

mark {
  background-color: #e3e3e0;
  border-radius: 4px;
  padding: 0 2px;
}
</style>
